import React, { useEffect, useState } from "react";
import api from "../api";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  for (const key of ["items", "data", "results"]) if (Array.isArray(value?.[key])) return value[key];
  return [];
};
const money = (value) => `PHP ${Number(value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
const sum = (rows, field) => rows.reduce((total, row) => total + Number(row[field] || 0), 0);
const occupied = (row) => [true, 1, "1"].includes(row.is_occupied);
const mapped = (row) => row.latitude !== null && row.longitude !== null && row.latitude !== "" && row.longitude !== "" && row.latitude !== undefined && row.longitude !== undefined && Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude)) && Math.abs(Number(row.latitude)) <= 90 && Math.abs(Number(row.longitude)) <= 180 && !(Number(row.latitude) === 0 && Number(row.longitude) === 0);

function Stats({title, note, cards}) {
  return <section className="dashboard-panel" aria-label={title}>
    <div className="dashboard-panel-title"><div><h2>{title}</h2>{note && <p className="text-sm text-slate-500">{note}</p>}</div></div>
    {cards ? <dl className="dashboard-mini-stats admin-summary-stats">{cards.map(([label, value]) => <div key={label}><dt><span>{label}</span></dt><dd><strong>{value}</strong></dd></div>)}</dl> : <p className="dashboard-empty">Statistics unavailable.</p>}
  </section>;
}

export default function AdminDashboardSummary({from, to}) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);
  const month = new Date().toLocaleString("en-US", {month:"long", year:"numeric"});
  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed([]);
    const sources = [
      ["attention", "/admin/operations/needs-attention", {from, to}],
      ["properties", "/rental-properties"],
      ["inquiries", "/admin/inquiries"],
      ["applications", "/admin/rental-applications"],
      ["payments", "/admin/payments"],
      ["balances", "/admin/payment-balances", {month_paid_for:month}],
      ["statement", "/admin/payments/statements", {month_paid_for:month}],
      ["management", "/admin/operations/management-report", {from, to}],
    ];
    Promise.allSettled(sources.map(([, url, params]) => api.get(url, {params}))).then((results) => {
      if (!active) return;
      const next = {}, errors = [];
      results.forEach((result, index) => {
        const key = sources[index][0];
        if (result.status === "fulfilled") next[key] = result.value?.data;
        else errors.push(key);
      });
      setData(next);
      setFailed(errors);
      setLoading(false);
    });
    return () => {active = false;};
  }, [from, to, month]);
  if (loading) return <p role="status" className="dashboard-empty">Loading operational statistics...</p>;
  const properties = asArray(data.properties), inquiries = asArray(data.inquiries), applications = asArray(data.applications);
  const payments = asArray(data.payments), balances = asArray(data.balances);
  const attention = data.attention, statement = data.statement?.summary;
  const management = data.management, aging = management?.payment_aging || {}, archived = management?.archive_counts || {};
  const paymentAlerts = asArray(attention?.payments).length, reportAlerts = asArray(attention?.reports).length, leaseAlerts = asArray(attention?.lease_alerts).length;
  return <div className="space-y-5">
    {failed.length > 0 && <p role="alert" className="ws-error">Some statistics could not be loaded ({failed.join(", ")}). Reload this page to retry.</p>}
    <div className="dashboard-secondary-grid">
      <Stats title="Needs Attention" note={`Selected period: ${from} to ${to}`} cards={attention && [
        ["Payment Alerts", paymentAlerts], ["Report Alerts", reportAlerts], ["Lease Alerts", leaseAlerts],
        ["Archive Events", asArray(attention.archive_activity).length], ["Total Focus Items", paymentAlerts + reportAlerts + leaseAlerts],
        ["Reminder Queue", attention.reminders_summary?.total || 0],
      ]}/>
      <Stats title="Properties and Map" note="Current portfolio" cards={data.properties && [
        ["Total", properties.length], ["Occupied", properties.filter(occupied).length], ["Vacant", properties.filter((row) => !occupied(row)).length],
        ["Assigned", properties.filter((row) => row.tenant || row.tenant_id).length],
        ["Lease Expiring", properties.filter((row) => ["expiring_7", "expiring_15", "expiring_30"].includes(row.lease_health)).length],
        ["Lease Expired", properties.filter((row) => row.lease_health === "expired").length],
        ["Mapped", properties.filter(mapped).length], ["Average Rent", money(properties.length ? sum(properties, "rent_price") / properties.length : 0)],
        ["Mapping Coverage", `${properties.length ? (properties.filter(mapped).length / properties.length * 100).toFixed(1) : "0.0"}%`],
      ]}/>
    </div>
    <div className="dashboard-secondary-grid">
      <Stats title="Leads Inbox" note="All leads" cards={data.inquiries && data.applications && [
        ["Total Leads", inquiries.length + applications.length], ["Inquiries", inquiries.length],
        ["Viewing Requests", inquiries.filter((row) => row.inquiry_type === "viewing").length], ["Applications", applications.length],
      ]}/>
      <Stats title="Payments" note={`Balance and pending: ${month}; collected and records: all submissions`} cards={data.payments && data.balances && [
        ["Balance", money(sum(balances, "remaining_balance"))], ["Collected", money(sum(payments.filter((row) => row.status === "approved"), "amount"))],
        ["Pending", money(sum(balances, "pending_paid"))], ["Records", payments.length],
      ]}/>
    </div>
    <div className="dashboard-secondary-grid">
      <Stats title="Payment Statements" note={month} cards={statement && [
        ["Expected", money(statement.expected_amount)], ["Approved", money(statement.approved_amount)],
        ["Pending", money(statement.pending_amount)], ["Remaining", money(statement.remaining_balance)],
      ]}/>
      <Stats title="Pending Payment Aging" note={`Selected period: ${from} to ${to}`} cards={management && [
        ["Under 24h", aging.under_24h || 0], ["24-72h", aging.between_24h_72h || 0], ["Over 72h", aging.over_72h || 0],
      ]}/>
    </div>
    <div className="dashboard-secondary-grid">
      <Stats title="Archived Records" cards={management && [
        ["Announcements", archived.announcements || 0], ["Reports", archived.reports || 0], ["Properties", archived.properties || 0], ["Tenants", archived.tenants || 0],
      ]}/>
      <Stats title="Report Activity" note={`Selected period: ${from} to ${to}`} cards={management && [
        ["Submitted", management.summary?.reports_submitted || 0], ["Resolved", management.summary?.reports_resolved || 0],
        ["Due Soon", management.summary?.sla_due_soon || 0], ["Overdue", management.summary?.sla_overdue || 0],
      ]}/>
    </div>
    {management && <section className="dashboard-panel" aria-label="Rental payment behavior">
      <div className="dashboard-panel-title"><div><h2>Rental payment behavior</h2><p className="text-sm text-slate-500">{(management.top_outstanding_meta?.criteria || []).join("; ") || "Rentals ranked by outstanding balance and payment behavior."}</p></div></div>
      {asArray(management.top_outstanding).length ? <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th>Rental</th><th>Remaining</th><th>Early payments</th><th>Late payments</th><th>Early rate</th></tr></thead><tbody>
        {management.top_outstanding.map((row) => <tr key={row.property_id}><td>{row.property_name} ({row.tenant_name || "No tenant"})</td><td>{money(row.remaining_amount)}</td><td>{row.early_payment_count || 0}</td><td>{row.late_payment_count || 0}</td><td>{Number(row.early_payment_rate || 0).toFixed(1)}%</td></tr>)}
      </tbody></table></div> : <p className="dashboard-empty">No rental payment behavior records for this period.</p>}
    </section>}
  </div>;
}
